import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MessageSquare, ArrowRight } from 'lucide-react';
import { ForumTopic } from '../../services/forumService';
import communityService from '../../services/communityService';
import { t } from '../../i18n';

// ============================================
// "Aus der Community" – horizontale Scroll-Sektion auf der Startseite.
// Neueste Forum-Themen als Karten im gleichen Stil wie HomeNewTournaments.
// Themen haben i. d. R. kein Bild → DartCloud-Platzhalter (/icon.png).
// Ersetzt das frühere ForumCompactList-Widget auf der HomePage.
// ============================================

const ACCENT = '#3fced1';

const topicLink = (topic: ForumTopic) =>
  topic.slug ? `/forum/topic/${topic.slug}` : '/forum';

/** Header-Bild des Themas (falls vorhanden), sonst erstes Bild-Medium. */
const topicImage = (topic: ForumTopic): string | null => {
  if (topic.headerImageUrl) return topic.headerImageUrl;
  const media = Array.isArray(topic.media) ? topic.media : [];
  const img = media.find((m) => m && m.type === 'image' && (m.thumbnailUrl || m.url));
  return img?.thumbnailUrl || img?.url || null;
};

const HomeForumTopics: React.FC<{ topics: ForumTopic[] }> = ({ topics }) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  if (topics.length === 0) return null;

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <section aria-label={t('web.component.forum-compact-list.aus-der-community')}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{t('web.component.forum-compact-list.aus-der-community')}</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/forum')}
            className="mr-1 hidden items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1] sm:flex"
          >
            {t('web.component.events-filter-bar.alle')}{' '}<ArrowRight size={13} />
          </button>
          {topics.length > 3 && (
            <>
              <button
                type="button"
                aria-label={t('web.component.chats-manager.zurueck')}
                onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                aria-label={t('web.component.home-darts-knowledge.weiter')}
                onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {topics.map((topic) => (
          <TopicCard
            key={topic.id || topic._id || topic.slug}
            topic={topic}
            onClick={() => navigate(topicLink(topic))}
          />
        ))}
      </div>
    </section>
  );
};

const TopicCard: React.FC<{ topic: ForumTopic; onClick: () => void }> = ({ topic, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const imageUrl = topicImage(topic);
  const showImage = imageUrl && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={imageUrl as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <img
              src="/icon.png"
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-lg shadow-[0_0_24px_rgba(63,206,210,0.25)]"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2.5">
        {/* Kategorie-Chip + relative Zeit über dem Titel */}
        <div className="mb-2 flex items-center justify-between gap-1.5">
          <span
            className="shrink-0 rounded px-2 py-0.5 text-[10px] font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            {topic.categoryName || t('web.component.footer.forum')}
          </span>
          {topic.createdAt && (
            <span className="truncate text-[10.5px] text-zinc-500">
              {communityService.formatRelativeTime(topic.createdAt)}
            </span>
          )}
        </div>
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {topic.title || t('web.component.forum-compact-list.ohne-titel')}
        </h3>
        <div className="mt-auto flex items-center gap-1 pt-1.5 text-[11px] text-gray-500">
          <MessageSquare size={11} />{' '}{t('web.component.home-forum-topics.antworten', { value: topic.postCount ?? 0 })}
        </div>
      </div>
    </button>
  );
};

export default HomeForumTopics;
